// src/pages/ClientAdminDashboard.tsx
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import AppLayout from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useEffectiveRole } from "@/hooks/useEffectiveRole";

import {
  Building2,
  Users,
  Shield,
  ClipboardList,
  ArrowRight,
  Layers,
} from "lucide-react";

import {
  fetchClientContext,
  type ClientContext,
} from "@/api/fetchClientContext";

type State = {
  loading: boolean;
  error: string | null;
  client: ClientContext | null;
};

export default function ClientAdminDashboard() {
  const navigate = useNavigate();
  const { role: effectiveRole } = useEffectiveRole();

  const [state, setState] = useState<State>({
    loading: true,
    error: null,
    client: null,
  });

  useEffect(() => {
    let active = true;

    (async () => {
      try {
        const ctx = await fetchClientContext();
        if (!active) return;

        if (!ctx) {
          setState({
            loading: false,
            error:
              "You are not currently linked to a client account. Please contact your CSV Pro administrator.",
            client: null,
          });
        } else {
          setState({
            loading: false,
            error: null,
            client: ctx,
          });
        }
      } catch (e: any) {
        if (!active) return;
        console.error("[ClientAdminDashboard] error:", e);
        setState({
          loading: false,
          error: e?.message || "Unable to load client context",
          client: null,
        });
      }
    })();

    return () => {
      active = false;
    };
  }, []);

  const { loading, error, client } = state;

  // --- loading state ---
  if (loading) {
    return (
      <AppLayout title="Client Admin — Loading">
        <div className="min-h-screen flex items-center justify-center">
          <div className="space-y-3 text-center">
            <div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin mx-auto" />
            <p className="text-sm text-muted-foreground">
              Loading your client workspace…
            </p>
          </div>
        </div>
      </AppLayout>
    );
  }

  // --- error / no-client state ---
  if (error || !client) {
    return (
      <AppLayout title="Client Admin">
        <div className="min-h-screen flex items-center justify-center px-4">
          <Card className="max-w-lg w-full">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-destructive" />
                <span>Client access issue</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                {error ||
                  "You do not appear to be assigned to a client workspace yet."}
              </p>
              <p className="text-xs text-muted-foreground">
                If you believe this is a mistake, please contact your CSV Pro
                administrator or engagement lead.
              </p>
            </CardContent>
          </Card>
        </div>
      </AppLayout>
    );
  }

  // --- normal render ---
  const isClientAdmin = client.roles.includes("client_admin");
  const isPortfolioManager = client.roles.includes("portfolio_manager");
  const isViewer = client.roles.includes("viewer");

  return (
    <AppLayout title={`Client Admin — ${client.client_name}`}>
      <div className="min-h-screen bg-background px-4 py-6 md:px-8 md:py-8">
        {/* Page header */}
        <header className="max-w-5xl mx-auto mb-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-2xl md:text-3xl font-semibold tracking-tight">
              {client.client_name}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Client workspace · Admin & portfolio view
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              {isClientAdmin && (
                <Badge variant="default" className="text-xs">
                  Client Admin
                </Badge>
              )}
              {isPortfolioManager && (
                <Badge variant="outline" className="text-xs">
                  Portfolio Manager
                </Badge>
              )}
              {isViewer && (
                <Badge variant="outline" className="text-xs">
                  Read-only Viewer
                </Badge>
              )}
            </div>
          </div>

          <div className="flex gap-2">
            {/* Future: add “Switch client” or “Invite user” actions here */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                if (effectiveRole === "portfolio_manager") {
                  navigate("/pm");
                } else if (effectiveRole === "admin") {
                  navigate("/admin");
                } else {
                  navigate("/dashboard");
                }
              }}
            >
              Back to CSV Pro Home
            </Button>
          </div>
        </header>

        {/* Main content grid – modeled after admin-style cards */}
        <main className="max-w-5xl mx-auto grid gap-4 md:grid-cols-2">
          {/* Sub-Organizations card - only for client_admin */}
          {isClientAdmin && (
            <Card className="h-full border-primary/20">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
                <div className="flex items-center gap-2">
                  <Layers className="h-4 w-4 text-primary" />
                  <CardTitle className="text-base font-semibold">
                    Sub-Organizations
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <p className="text-muted-foreground">
                  Create and manage sub-organizations to group portfolios within{" "}
                  <span className="font-medium">{client.client_name}</span>.
                </p>
                <Button
                  variant="default"
                  size="sm"
                  className="mt-2 inline-flex items-center gap-1"
                  onClick={() => navigate("/client/sub-orgs")}
                >
                  Manage Sub-Organizations
                  <ArrowRight className="h-3 w-3" />
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Organizations card */}
          <Card className="h-full">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4 text-muted-foreground" />
                <CardTitle className="text-base font-semibold">
                  Organizations
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <p className="text-muted-foreground">
                View and manage investee organizations for this client (e.g.
                MNOs, telecoms, data centers, PE/VC funds).
              </p>
              <Button
                variant="outline"
                size="sm"
                className="mt-2 inline-flex items-center gap-1"
                onClick={() => navigate("/org-admin")}
              >
                Open organizations
                <ArrowRight className="h-3 w-3" />
              </Button>
            </CardContent>
          </Card>

          {/* Users & roles card */}
          <Card className="h-full">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-muted-foreground" />
                <CardTitle className="text-base font-semibold">
                  Users & roles
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <p className="text-muted-foreground">
                Assign Respondents, Reviewers, and portfolio users to
                organizations under <span className="font-medium">
                  {client.client_name}
                </span>
                .
              </p>
              <Button
                variant="outline"
                size="sm"
                className="mt-2 inline-flex items-center gap-1"
                // TODO: wire this later to a real user-management view
                onClick={() => {
                  /* navigate("/client-users") */
                }}
                disabled
              >
                User management (coming soon)
              </Button>
            </CardContent>
          </Card>

          {/* Assessments card */}
          <Card className="h-full">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
              <div className="flex items-center gap-2">
                <ClipboardList className="h-4 w-4 text-muted-foreground" />
                <CardTitle className="text-base font-semibold">
                  Assessments
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <p className="text-muted-foreground">
                Track active and completed cybersecurity assessments across all
                organizations in this client portfolio.
              </p>
              <Button
                variant="outline"
                size="sm"
                className="mt-2 inline-flex items-center gap-1"
                onClick={() => {
                  /* TODO – link to client-level assessment list */
                }}
                disabled
              >
                View assessments (coming soon)
              </Button>
            </CardContent>
          </Card>

          {/* Placeholder for future analytics / reports */}
          <Card className="h-full">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-3">
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4 text-muted-foreground" />
                <CardTitle className="text-base font-semibold">
                  Portfolio insights
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <p className="text-muted-foreground">
                In a later step, this will surface portfolio-level scores,
                trends, and AI-generated risk summaries.
              </p>
              <Badge variant="outline" className="text-[10px] uppercase">
                Roadmap
              </Badge>
            </CardContent>
          </Card>
        </main>
      </div>
    </AppLayout>
  );
}
